import { createFileRoute } from '@tanstack/react-router';
import dayjs from 'dayjs';
import {
  BattalionRow,
  CountBadge,
  DashCard,
  DashedAlert,
  DashGreeting,
  DashLink,
  DashRow,
  KpiStrip,
  OperationHero,
  OperationRow,
  PendingRow,
  RecentSoldierRow,
  ShirtSizes,
  SourceBar,
} from '@/components/ui/admin/dashboard';
import { Content } from '@/components/ui/content';
import { Icon } from '@/components/ui/icon';
import { useRouter } from '@/hooks/use-router';
import { date } from '@/lib/date';
import { meta } from '@/lib/meta';
import { useIsAuthenticated } from '@/services/auth';
import { type DashboardData, useDashboard } from '@/services/dashboard';

export const Route = createFileRoute('/_app/')({
  head: () => meta({ label: 'Dashboard' }),
  component: RouteComponent,
});

const BREADCRUMBS = {
  title: 'Dashboard',
  items: [{ label: 'Comando' }, { label: 'Visão geral', active: true }],
};

const HOW_HEARD_ABOUT_LABELS: Record<string, string> = {
  church: 'Igreja/paróquia',
  referral: 'Indicação',
  social_media: 'Redes sociais',
  other: 'Outro',
};

/** Numéricos (infantis) primeiro, depois as letras na ordem de grade. */
const SHIRT_SIZE_ORDER = ['PP', 'P', 'M', 'G', 'GG', 'XG', 'G1', 'G2', 'G3'];

const pad = (n: number) => (n < 10 ? `0${n}` : String(n));

/** Data pura ("YYYY-MM-DD") — o Eden pode entregar como `Date` em UTC; lê em UTC pra não voltar um dia. */
const ageFrom = (birthday: string | Date) =>
  date().diff(dayjs.utc(birthday).format('YYYY-MM-DD'), 'year');

const relativeTime = (value: string | Date) => {
  const at = date(value);
  const now = date();
  const diffMin = now.diff(at, 'minute');

  if (diffMin < 60) return `Há ${Math.max(diffMin, 1)} min`;
  if (at.isSame(now, 'day')) return `Há ${now.diff(at, 'hour')} h`;
  if (at.isSame(now.subtract(1, 'day'), 'day')) return `Ontem, ${at.format('HH:mm')}`;
  return at.format('DD/MM, HH:mm');
};

/**
 * Abre a listagem de recrutas já filtrada: grava a busca no mesmo formato que o
 * `SearchToolbar` restaura da `sessionStorage` ao montar a rota.
 */
const soldiersSearch = (values: Record<string, string>) => {
  sessionStorage.setItem(
    'search',
    JSON.stringify({ pathname: '/soldiers', values: { search: '', ...values } }),
  );
  return '/soldiers';
};

const sortShirtSizes = (sizes: { size: string; count: number }[]) =>
  [...sizes].sort((a, b) => {
    const na = Number(a.size);
    const nb = Number(b.size);
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
    if (!Number.isNaN(na)) return -1;
    if (!Number.isNaN(nb)) return 1;
    const ia = SHIRT_SIZE_ORDER.indexOf(a.size.toUpperCase());
    const ib = SHIRT_SIZE_ORDER.indexOf(b.size.toUpperCase());
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });

/** Placeholder do fonte (`/user.jpg`) não é foto de verdade — mostra as iniciais. */
const realPhoto = (photo: string | null) =>
  photo && photo !== '/user.jpg' ? photo : undefined;

const EmptyNote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="px-6 py-8 text-center text-[12.5px] text-faint">{children}</span>
);

function RouteComponent() {
  const { user } = useIsAuthenticated();
  const { data, isLoading } = useDashboard();

  const today = date().format('dddd, DD [de] MMMM [de] YYYY');

  return (
    <Content isLoading={isLoading} breadcrumbs={BREADCRUMBS}>
      <div className="grid max-w-[1480px] grid-cols-[minmax(0,1fr)] gap-5">
        <DashGreeting date={today} name={user?.name?.split(' ')[0] ?? 'recruta'} />
        {data && <Dashboard data={data} />}
      </div>
    </Content>
  );
}

function Dashboard({ data }: { data: DashboardData }) {
  const router = useRouter();
  const { operation, history, stats } = data;

  if (!operation || !stats) {
    return (
      <DashCard
        title="Nenhuma operação"
        subtitle="Cadastre um recrutamento para ver o efetivo."
      >
        <EmptyNote>Sem recrutamentos cadastrados.</EmptyNote>
      </DashCard>
    );
  }

  const operationDate = date(operation.date);
  const daysLeft = Math.max(
    0,
    operationDate.startOf('day').diff(date().startOf('day'), 'day'),
  );
  const maxBataillon = Math.max(0, ...stats.bataillons.map((b) => b.count));
  const declaredSources = stats.howHeardAbout.reduce((sum, s) => sum + s.count, 0);
  const unpaid = stats.enrolled - stats.paid;
  const paidPct = stats.enrolled ? Math.round((stats.paid / stats.enrolled) * 100) : 0;
  const ridePct = stats.enrolled
    ? Math.round((stats.needRide / stats.enrolled) * 100)
    : 0;
  const pendingTotal =
    stats.withoutBataillon +
    unpaid +
    stats.withoutEmergencyContact +
    stats.notEnterInContact;

  const goSoldiers = () => router.push(soldiersSearch({ eventId: operation.id }));
  // Sem recrutamento no filtro — a busca salva impede o filtro padrão (próxima operação).
  const goAllSoldiers = () => router.push(soldiersSearch({}));
  const chevron = <Icon name="chevron-right" className="size-4" />;
  const pin = <Icon name="map-pin" className="size-3.5" />;

  return (
    <>
      <DashRow>
        <OperationHero
          name={operation.name}
          dateLabel={operation.period}
          city={operation.city}
          daysLeft={daysLeft}
          open={operation.open}
          enrolled={stats.enrolled}
          paid={stats.paid}
          coverUrl="/assets/military-bg.jpg"
          calendarIcon={<Icon name="calendar" className="size-4" />}
          pinIcon={<Icon name="map-pin" className="size-4" />}
          actions={
            <>
              <button
                type="button"
                onClick={goSoldiers}
                className="flex h-11 items-center gap-2 rounded-full bg-sand px-[22px] text-[13.5px] font-bold text-on-sand hover:opacity-90"
              >
                <Icon name="users" className="size-4" />
                Ver recrutas
              </button>
              <button
                type="button"
                onClick={() => router.push(`/events/${operation.id}`)}
                className="flex h-11 items-center gap-2 rounded-full border border-border px-[22px] text-[13.5px] font-semibold text-foreground hover:bg-raised"
              >
                <Icon name="pencil" className="size-4" />
                Editar recrutamento
              </button>
            </>
          }
        />

        <DashCard
          size="narrow"
          title="Batalhões"
          subtitle="Recrutas das operações já realizadas."
          action={
            <button
              type="button"
              onClick={() => router.push('/bataillons')}
              aria-label="Ver batalhões"
              className="text-faint hover:text-sand"
            >
              {chevron}
            </button>
          }
        >
          <div className="flex flex-1 flex-col gap-4 px-6 py-5">
            {stats.bataillons.length ? (
              stats.bataillons.map((b) => (
                <BattalionRow
                  key={b.id}
                  tag={b.name.slice(0, 2).toUpperCase()}
                  name={b.name}
                  count={b.count}
                  max={maxBataillon}
                />
              ))
            ) : (
              <EmptyNote>Nenhum batalhão ativo.</EmptyNote>
            )}
          </div>
          {stats.withoutBataillon > 0 && (
            <DashedAlert
              icon={<Icon name="circle-alert" className="size-4" />}
              action={
                <button
                  type="button"
                  onClick={goAllSoldiers}
                  className="col-label text-sand hover:opacity-85"
                >
                  Distribuir
                </button>
              }
            >
              <strong className="text-white">
                {stats.withoutBataillon}{' '}
                {stats.withoutBataillon === 1 ? 'recruta' : 'recrutas'}
              </strong>{' '}
              ainda sem batalhão
            </DashedAlert>
          )}
        </DashCard>
      </DashRow>

      <KpiStrip
        items={[
          {
            label: 'Recrutas inscritos',
            value: pad(stats.enrolled),
            note: `+${stats.thisWeek} esta semana`,
            noteTone: 'olive',
            icon: <Icon name="users" className="size-4" />,
          },
          {
            label: 'Pagamento confirmado',
            value: `${paidPct}%`,
            note: `${stats.paid} de ${stats.enrolled}`,
            icon: <Icon name="circle-check" className="size-4" />,
          },
          {
            label: 'Precisam de carona',
            value: pad(stats.needRide),
            note: `${ridePct}% do efetivo`,
            icon: <Icon name="map-pin" className="size-4" />,
          },
          {
            label: 'Restrição de saúde',
            value: pad(stats.healthProblems),
            note: stats.healthProblems ? 'Revisar fichas' : 'Nenhuma ficha',
            noteTone: stats.healthProblems ? 'sand' : 'muted',
            icon: <Icon name="heart-pulse" className="size-4" />,
            iconTone: 'danger',
          },
        ]}
      />

      <DashRow>
        <DashCard
          size="wide"
          title="Histórico de operações"
          subtitle="Recrutamentos realizados e em andamento."
          action={
            <DashLink onClick={() => router.push('/events')} icon={chevron}>
              Ver todos
            </DashLink>
          }
        >
          {history.map((event) => {
            const eventDate = date(event.date);
            return (
              <OperationRow
                key={event.id}
                day={eventDate.format('DD')}
                month={eventDate.format('MMM')}
                year={eventDate.format('YYYY')}
                name={event.name}
                city={event.city}
                soldiers={event.soldiers}
                open={event.upcoming}
                pinIcon={pin}
                onClick={() => router.push(`/events/${event.id}`)}
              />
            );
          })}
        </DashCard>

        <DashCard
          size="narrow"
          title="Como conheceram"
          subtitle="Origem declarada na inscrição."
        >
          {stats.howHeardAbout.length ? (
            <div className="flex flex-col gap-5 px-6 py-5">
              {stats.howHeardAbout.map((s, index) => (
                <SourceBar
                  key={s.source}
                  label={HOW_HEARD_ABOUT_LABELS[s.source ?? ''] ?? s.source ?? ''}
                  count={s.count}
                  total={declaredSources}
                  lead={index === 0}
                />
              ))}
            </div>
          ) : (
            <EmptyNote>Nenhum recruta declarou a origem.</EmptyNote>
          )}
        </DashCard>
      </DashRow>

      <DashRow align="start">
        <DashCard
          size="wide"
          title="Últimas inscrições"
          subtitle="Recrutas mais recentes da operação."
          action={
            <DashLink onClick={goSoldiers} icon={chevron}>
              Ver todos
            </DashLink>
          }
        >
          {stats.recentSoldiers.length ? (
            <div className="overflow-x-auto">
              <div className="min-w-[600px]">
                {stats.recentSoldiers.map((s) => (
                  <RecentSoldierRow
                    key={s.id}
                    photo={realPhoto(s.photo)}
                    name={s.name.trim()}
                    number={s.soldierNumber}
                    age={ageFrom(s.birthday)}
                    city={s.city}
                    shirtSize={s.shirtSize || '—'}
                    paid={!!s.paid}
                    when={relativeTime(s.createdAt)}
                    onClick={() => router.push(`/soldiers/${s.id}`)}
                  />
                ))}
              </div>
            </div>
          ) : (
            <EmptyNote>Nenhuma inscrição nesta operação.</EmptyNote>
          )}
        </DashCard>

        <div className="flex min-w-0 flex-[1_1_320px] flex-col gap-5">
          <DashCard title="Pendências" action={<CountBadge value={pendingTotal} />}>
            <PendingRow
              label="Sem batalhão atribuído"
              hint="Operações já realizadas"
              count={stats.withoutBataillon}
              tone="sand"
              chevron={chevron}
              onClick={goAllSoldiers}
            />
            <PendingRow
              label="Pagamento pendente"
              hint="Confirmar com o recruta"
              count={unpaid}
              tone="sand"
              chevron={chevron}
              onClick={goSoldiers}
            />
            <PendingRow
              label="Sem contato de emergência"
              hint="Obrigatório para a operação"
              count={stats.withoutEmergencyContact}
              tone="danger"
              chevron={chevron}
              onClick={goSoldiers}
            />
            <PendingRow
              label="Não autorizam contato"
              hint="Excluir das campanhas"
              count={stats.notEnterInContact}
              tone="faint"
              chevron={chevron}
              onClick={goSoldiers}
            />
          </DashCard>

          {stats.shirtSizes.length > 0 && (
            <ShirtSizes sizes={sortShirtSizes(stats.shirtSizes)} />
          )}
        </div>
      </DashRow>
    </>
  );
}
